// Import pt-BR translations
import ptBRApp from './pt-BR/app.json';
import ptBRLockScreen from './pt-BR/lock_screen.json';
import ptBRBootScreen from './pt-BR/boot_screen.json';
import ptBRHomeScreen from './pt-BR/home_screen.json';
import ptBRAboutMe from './pt-BR/about_me.json';
import ptBRProjects from './pt-BR/projects.json';
import ptBRProjectsData from './pt-BR/projects_data.json';
import ptBRFinder from './pt-BR/finder.json';
import ptBRGallery from './pt-BR/gallery.json';
import ptBRNotifications from './pt-BR/notifications.json';
import ptBRFaq from './pt-BR/faq.json';
import ptBREmail from './pt-BR/email.json';
import ptBRCursedAlert from './pt-BR/cursed_alert.json';
import ptBRCatAlert from './pt-BR/cat_alert.json';
import ptBRMobileWarning from './pt-BR/mobile_warning.json';

// Import en-US translations
import enUSApp from './en-US/app.json';
import enUSLockScreen from './en-US/lock_screen.json';
import enUSBootScreen from './en-US/boot_screen.json';
import enUSHomeScreen from './en-US/home_screen.json';
import enUSAboutMe from './en-US/about_me.json';
import enUSProjects from './en-US/projects.json';
import enUSProjectsData from './en-US/projects_data.json';
import enUSFinder from './en-US/finder.json';
import enUSGallery from './en-US/gallery.json';
import enUSNotifications from './en-US/notifications.json';
import enUSFaq from './en-US/faq.json';
import enUSEmail from './en-US/email.json';
import enUSCursedAlert from './en-US/cursed_alert.json';
import enUSCatAlert from './en-US/cat_alert.json';
import enUSMobileWarning from './en-US/mobile_warning.json';

// Define translation structure
interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

// Organize translations by locale
const locales: { [locale: string]: Translations } = {
  'pt-BR': {
    app: ptBRApp,
    lock_screen: ptBRLockScreen,
    boot_screen: ptBRBootScreen,
    home_screen: ptBRHomeScreen,
    about_me: ptBRAboutMe,
    projects: ptBRProjects,
    projects_data: ptBRProjectsData,
    finder: ptBRFinder,
    gallery: ptBRGallery,
    notifications: ptBRNotifications,
    faq: ptBRFaq,
    email: ptBREmail,
    cursed_alert: ptBRCursedAlert,
    cat_alert: ptBRCatAlert,
    mobile_warning: ptBRMobileWarning
  },
  'en-US': {
    app: enUSApp,
    lock_screen: enUSLockScreen,
    boot_screen: enUSBootScreen,
    home_screen: enUSHomeScreen,
    about_me: enUSAboutMe,
    projects: enUSProjects,
    projects_data: enUSProjectsData,
    finder: enUSFinder,
    gallery: enUSGallery,
    notifications: enUSNotifications,
    faq: enUSFaq,
    email: enUSEmail,
    cursed_alert: enUSCursedAlert,
    cat_alert: enUSCatAlert,
    mobile_warning: enUSMobileWarning
  },
};

// Get browser language and normalize
const getBrowserLanguage = (): string => {
  // Check localStorage first
  const savedLanguage = localStorage.getItem('preferredLanguage');
  if (savedLanguage === 'pt-BR' || savedLanguage === 'en-US') {
    return savedLanguage;
  }
  
  const browserLang = navigator.language;
  
  // Normalize language codes
  if (browserLang.toLowerCase().startsWith('pt')) {
    return 'pt-BR';
  }
  if (browserLang.toLowerCase().startsWith('en')) {
    return 'en-US';
  }
  
  return 'en-US'; // Default fallback
};

// Current language (can be changed dynamically)
let currentLanguage = getBrowserLanguage();

// Language change listeners
type LanguageChangeListener = () => void;
const languageChangeListeners: LanguageChangeListener[] = [];

export function translations(key: string): string {
  const parts = key.split('.');
  
  if (parts.length < 2) return key;
  
  const [screen, ...rest] = parts;
  const screenTranslations = locales[currentLanguage]?.[screen];
  
  if (!screenTranslations) return key;
  
  const translationKey = rest.join('.');
  const value = screenTranslations[translationKey];
  
  return typeof value === 'string' ? value : key;
}

export function setLanguage(language: 'pt-BR' | 'en-US'): void {
  currentLanguage = language;
  localStorage.setItem('preferredLanguage', language);
  languageChangeListeners.forEach(listener => listener());
}

export function onLanguageChange(listener: LanguageChangeListener): () => void {
  languageChangeListeners.push(listener);
  return () => {
    const index = languageChangeListeners.indexOf(listener);
    if (index > -1) {
      languageChangeListeners.splice(index, 1);
    }
  };
}

export function getCurrentLanguage(): string {
  return currentLanguage;
}

export { t } from '../i18n';
